import moment from 'moment-timezone'
import {
  getExclusionPeriod,
  createExclusionPeriod,
  updateExclusionPeriod,
  deleteExclusionPeriod,
  getExclusionPeriodTemplate
} from '@/services/revenue-exclusion-period-service'
import { getTransportationProfile } from '@/services/transportation-profiles-service'
import { addAlert, initBreadcrumbs, isAuthorized } from '@/utilities/helpers'

const MANUAL_HOLIDAY_KEYS = ['eid-al-fitr', 'eid-al-adha']

function toDateInput (value) {
  if (!value) {
    return null
  }
  const asMoment = moment.utc(value)
  return asMoment.isValid() ? asMoment.format('YYYY-MM-DD') : null
}

function toStartIso (value) {
  if (!value) {
    return null
  }

  const asMoment = moment.utc(value, 'YYYY-MM-DD', true)
  return asMoment.isValid() ? asMoment.startOf('day').toISOString() : null
}

function toEndIso (value) {
  if (!value) {
    return null
  }

  const asMoment = moment.utc(value, 'YYYY-MM-DD', true)
  return asMoment.isValid() ? asMoment.endOf('day').toISOString() : null
}

export default {
  name: 'advanced-revenue-exclusion-periods-period',
  props: {
    signedInUser: Object,
    exclusionPeriodId: String
  },
  data () {
    return {
      apiInProgress: false,
      breadcrumbs: [],
      form: getExclusionPeriodTemplate(),
      selectedTransportationProfile: null
    }
  },
  computed: {
    isNewRecord () {
      return !this.exclusionPeriodId || this.exclusionPeriodId === '0'
    },
    isAdmin () {
      return isAuthorized(this.signedInUser, 'admin')
    },
    agencyOptions () {
      return this.$store.getters.getAgencies || []
    },
    recurrenceTypeOptions () {
      return ['once', 'weekly', 'monthly', 'yearly']
    },
    dayOfWeekOptions () {
      return [
        { text: 'Sunday', value: 0 },
        { text: 'Monday', value: 1 },
        { text: 'Tuesday', value: 2 },
        { text: 'Wednesday', value: 3 },
        { text: 'Thursday', value: 4 },
        { text: 'Friday', value: 5 },
        { text: 'Saturday', value: 6 }
      ]
    },
    monthOptions () {
      return [
        { text: 'January', value: 1 },
        { text: 'February', value: 2 },
        { text: 'March', value: 3 },
        { text: 'April', value: 4 },
        { text: 'May', value: 5 },
        { text: 'June', value: 6 },
        { text: 'July', value: 7 },
        { text: 'August', value: 8 },
        { text: 'September', value: 9 },
        { text: 'October', value: 10 },
        { text: 'November', value: 11 },
        { text: 'December', value: 12 }
      ]
    },
    holidayKeyOptions () {
      return ['good-friday', 'easter', 'easter-monday', 'eid-al-fitr', 'eid-al-adha', 'farmers-day']
    },
    useHolidayKey: {
      get () {
        return !!this.form.holidayKey
      },
      set (value) {
        if (value) {
          this.form.monthStart = null
          this.form.monthEnd = null
          this.form.dayOfMonthStart = null
          this.form.dayOfMonthEnd = null
          if (!this.form.holidayKey) {
            this.form.holidayKey = 'easter'
          }
          if (this.form.holidayOffsetDays === null || this.form.holidayOffsetDays === undefined) {
            this.form.holidayOffsetDays = 0
          }
          if (this.form.holidayDurationDays === null || this.form.holidayDurationDays === undefined) {
            this.form.holidayDurationDays = 1
          }
        } else {
          this.form.holidayKey = null
          this.form.holidayOffsetDays = 0
          this.form.holidayDurationDays = 1
        }
      }
    },
    isManualHolidayKey () {
      return MANUAL_HOLIDAY_KEYS.indexOf(String(this.form.holidayKey || '').toLowerCase()) !== -1
    }
  },
  async mounted () {
    this.breadcrumbs = initBreadcrumbs.bind(this)()
    await this.loadRecord()
  },
  methods: {
    async loadRecord () {
      this.apiInProgress = true
      try {
        if (this.isNewRecord) {
          this.form = Object.assign({}, getExclusionPeriodTemplate(), {
            periodStartDate: null,
            periodEndDate: null
          })
          this.selectedTransportationProfile = null
          return
        }

        const record = await getExclusionPeriod({ id: this.exclusionPeriodId })
        this.form = Object.assign({}, record || {}, {
          periodStartDate: toDateInput(record && record.periodStart),
          periodEndDate: toDateInput(record && record.periodEnd),
          holidayOffsetDays: record && record.holidayOffsetDays !== undefined && record.holidayOffsetDays !== null ? Number(record.holidayOffsetDays) : 0,
          holidayDurationDays: record && record.holidayDurationDays !== undefined && record.holidayDurationDays !== null ? Number(record.holidayDurationDays) : 1
        })

        if (record && record.transportationProfileId) {
          const transportationProfile = await getTransportationProfile({
            id: record.transportationProfileId,
            filter: {
              include: [{ currentDriverObj: 'stUser' }]
            }
          })

          if (transportationProfile) {
            transportationProfile.currentDriverName = transportationProfile.currentDriverObj && transportationProfile.currentDriverObj.stUser
              ? ((transportationProfile.currentDriverObj.stUser.firstName || '') + ' ' + (transportationProfile.currentDriverObj.stUser.lastName || '')).trim()
              : ''
            this.selectedTransportationProfile = transportationProfile
          }
        }
      } catch (err) {
        addAlert({
          message: err && err.message ? err.message : 'Unable to load exclusion period',
          type: 'error'
        })
      } finally {
        this.apiInProgress = false
      }
    },
    buildPayload () {
      const payload = {
        agencyId: this.form.agencyId || null,
        recurrenceType: this.form.recurrenceType || 'once',
        transportationProfileId: this.selectedTransportationProfile ? this.selectedTransportationProfile.id : null,
        reason: this.form.reason || null,
        isActive: !!this.form.isActive,
        periodStart: toStartIso(this.form.periodStartDate),
        periodEnd: toEndIso(this.form.periodEndDate),
        dayOfWeekStart: this.form.dayOfWeekStart !== null && this.form.dayOfWeekStart !== undefined ? Number(this.form.dayOfWeekStart) : null,
        dayOfWeekEnd: this.form.dayOfWeekEnd !== null && this.form.dayOfWeekEnd !== undefined ? Number(this.form.dayOfWeekEnd) : null,
        dayOfMonthStart: this.form.dayOfMonthStart !== null && this.form.dayOfMonthStart !== undefined ? Number(this.form.dayOfMonthStart) : null,
        dayOfMonthEnd: this.form.dayOfMonthEnd !== null && this.form.dayOfMonthEnd !== undefined ? Number(this.form.dayOfMonthEnd) : null,
        monthStart: this.form.monthStart !== null && this.form.monthStart !== undefined ? Number(this.form.monthStart) : null,
        monthEnd: this.form.monthEnd !== null && this.form.monthEnd !== undefined ? Number(this.form.monthEnd) : null,
        holidayKey: this.form.holidayKey || null,
        holidayOffsetDays: this.form.holidayOffsetDays !== null && this.form.holidayOffsetDays !== undefined ? Number(this.form.holidayOffsetDays) : 0,
        holidayDurationDays: this.form.holidayDurationDays !== null && this.form.holidayDurationDays !== undefined ? Number(this.form.holidayDurationDays) : 1
      }

      if (payload.recurrenceType === 'once') {
        payload.dayOfWeekStart = null
        payload.dayOfWeekEnd = null
        payload.dayOfMonthStart = null
        payload.dayOfMonthEnd = null
        payload.monthStart = null
        payload.monthEnd = null
        payload.holidayKey = null
        payload.holidayOffsetDays = 0
        payload.holidayDurationDays = 1
      } else if (payload.recurrenceType === 'weekly') {
        payload.dayOfMonthStart = null
        payload.dayOfMonthEnd = null
        payload.monthStart = null
        payload.monthEnd = null
        payload.holidayKey = null
        payload.holidayOffsetDays = 0
        payload.holidayDurationDays = 1
      } else if (payload.recurrenceType === 'monthly') {
        payload.dayOfWeekStart = null
        payload.dayOfWeekEnd = null
        payload.monthStart = null
        payload.monthEnd = null
        payload.holidayKey = null
        payload.holidayOffsetDays = 0
        payload.holidayDurationDays = 1
      } else if (payload.recurrenceType === 'yearly') {
        payload.dayOfWeekStart = null
        payload.dayOfWeekEnd = null
        if (payload.holidayKey) {
          payload.monthStart = null
          payload.monthEnd = null
          payload.dayOfMonthStart = null
          payload.dayOfMonthEnd = null
          payload.holidayDurationDays = payload.holidayDurationDays < 1 ? 1 : payload.holidayDurationDays
        } else {
          payload.holidayOffsetDays = 0
          payload.holidayDurationDays = 1
        }
      }

      return payload
    },
    validatePayload (payload) {
      if (!payload.agencyId) {
        addAlert({ message: 'Agency ID is required', type: 'warning' })
        return false
      }

      if (!payload.recurrenceType) {
        addAlert({ message: 'Recurrence type is required', type: 'warning' })
        return false
      }

      if (payload.recurrenceType === 'once') {
        if (!payload.periodStart || !payload.periodEnd) {
          addAlert({ message: 'Period start and period end are required for once recurrence', type: 'warning' })
          return false
        }
      }

      if (payload.recurrenceType === 'weekly' && (payload.dayOfWeekStart === null || payload.dayOfWeekStart === undefined)) {
        addAlert({ message: 'Day of week start is required for weekly recurrence', type: 'warning' })
        return false
      }

      if (payload.recurrenceType === 'monthly' && (payload.dayOfMonthStart === null || payload.dayOfMonthStart === undefined)) {
        addAlert({ message: 'Day of month start is required for monthly recurrence', type: 'warning' })
        return false
      }

      if (payload.recurrenceType === 'yearly' && !payload.holidayKey && (payload.monthStart === null || payload.dayOfMonthStart === null)) {
        addAlert({ message: 'Month start and day of month start are required for yearly recurrence', type: 'warning' })
        return false
      }

      return true
    },
    async saveRecord () {
      if (!this.isAdmin) {
        return
      }

      const payload = this.buildPayload()

      if (!this.validatePayload(payload)) {
        return
      }

      this.apiInProgress = true
      try {
        if (this.isNewRecord) {
          await createExclusionPeriod(payload)
          addAlert({ message: 'Revenue exclusion period created', type: 'success' })
          this.navigateToExclusionList({ reload: true })
        } else {
          payload.id = this.exclusionPeriodId
          await updateExclusionPeriod(payload)
          addAlert({ message: 'Revenue exclusion period updated', type: 'success' })
          await this.loadRecord()
        }
      } catch (err) {
        addAlert({
          message: err && err.message ? err.message : 'Unable to save exclusion period',
          type: 'error'
        })
      } finally {
        this.apiInProgress = false
      }
    },
    async deleteRecord () {
      if (!this.isAdmin || this.isNewRecord) {
        return
      }

      if (!confirm('Confirm deleting this revenue exclusion period?')) {
        return
      }

      this.apiInProgress = true
      try {
        await deleteExclusionPeriod({ id: this.exclusionPeriodId })
        addAlert({ message: 'Revenue exclusion period deleted', type: 'success' })
        this.navigateToExclusionList({ reload: true })
      } catch (err) {
        addAlert({
          message: err && err.message ? err.message : 'Unable to delete exclusion period',
          type: 'error'
        })
      } finally {
        this.apiInProgress = false
      }
    },
    navigateToExclusionList ({ reload } = {}) {
      const query = Object.assign({}, this.$route.query || {})
      if (reload) {
        query.refreshKey = String(Date.now())
      }
      this.$router.push({
        name: 'advanced-revenue-exclusion-periods',
        query
      })
    }
  }
}
